import React, { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import cn from 'classnames';

import {
  ClaimBookingOTPStatus,
  OtpVerificationResult,
  OTPStatus,
  OTPType,
} from '@/components/ReactivationFlowContainer/types/LookupBookingForClaimTypes';
import { useAuth } from '@/helpers/authUtils';

import { obfuscateEmail } from '@/helpers/dataMasking';
import { logError, logWarn } from '@/helpers/logger/logUtils';
import { Session } from '@/helpers/signinUtils';
import useReCaptchaContext from '@/hooks/useReCaptchaContext';
import useRecaptchaValidation from '@/hooks/useRecaptchaValidation';

import { selectFeatureFlags } from '@/reducers/common/selectors';

import { useClaimBookingLookup } from './hooks/useClaimBookingLookup';
import styles from './ReactivationFlowContainer.module.scss';

import {
  ClaimBookingDetailsConfirmation,
  ClaimBookingError,
  ClaimBookingForm,
  ClaimBookingOtpLocked,
  ClaimBookingOtpMethodSelect,
  ClaimBookingOtpSent,
  ClaimBookingOTPSuccess,
  ClaimBookingSailorSelect,
  ClaimImportError,
  ClaimImportingInProgress,
  ReclaimAccount,
  ClaimBookingSessionExpired,
} from './steps';

import NotificationError from '../NotificationError';
import Spinner from '../Spinner';
import { WizardContainer } from '../Wizard';

import {
  getCurrentClaimedSailor,
  mapLookupToClaimResult,
  mapSailorToSelectedSailorData,
} from './helpers/claimBookingHelper';
import isTokenExpiredError from './helpers/isTokenExpiredError';
import ClaimBookingDobUpdateOTP from './steps/ClaimBookingDobUpdateOTP';
import ClaimBookingEmailSelectOTP from './steps/ClaimBookingEmailSelectOTP';
import { CLAIM_BOOKING_OTP_RESOURCES } from './steps/ClaimBookingOtp.resources';
import {
  CLAIM_BOOKING_STEPS,
  CLAIM_BOOKING_ALL_STEPS as claimStep,
  DETAILS_MANUAL_ENTRY_FORM,
  IMPORT_ERROR,
  IMPORT_IN_PROGRESS,
  RECLAIM_ACCOUNT,
} from './steps/constants';
import NoContactDetail from './steps/NoContactDetail';
import {
  ClaimBookingResult,
  ContactMethod,
  EligibilityStatus,
  MatchingClient,
  SelectedSailorData,
  StepData,
} from './types/ClaimBookingTypes';

interface ReactivationOTPFlowContainerProps {
  backToLogin: () => void;
  initialStep?: string;
  initialStepData?: StepData;
}

const CONNECTION_ERROR_MESSAGE = 'Connection error, please try again.';

const ReactivationOTPFlowContainer: React.FC<ReactivationOTPFlowContainerProps> = ({
  backToLogin,
  initialStep = claimStep.RECLAIM_ACCOUNT,
  initialStepData,
}) => {
  const [step, setStep] = useState<{ stepId: string; data: StepData }>({
    stepId: initialStep,
    data: initialStepData || {},
  });

  const Auth = useAuth();
  const [currentUser, setCurrentUserAuth] = useState<Session | undefined>(undefined);
  const { isRecaptchaTestingEnabled = false } = useSelector(selectFeatureFlags);

  const [generalError, setGeneralError] = useState<string | null>(null);
  const [selectedSailorData, setSelectedSailorData] = useState<StepData | null>(null);
  const { isLoaded: isReCaptchaLoaded } = useReCaptchaContext();

  const getOtpType = (data: StepData) => (data.selectedMethod?.type === 'email' ? OTPType.EMAIL : OTPType.PHONE);

  const {
    runLookupBookingForClaim,
    generateClaimBookingOTP,
    claimBookingLookup,
    loadingLookup,
    loadingGenerateOtp,
    loadingClaimLookup,
    postClaimSailorProfileUpdate,
    loadingPostClaimProfile,
  } = useClaimBookingLookup();

  useEffect(() => {
    const fetchAuth = async () => {
      try {
        const auth = await Auth?.getAuth();
        setCurrentUserAuth(auth ? (auth as Session) : undefined);
      } catch (error: unknown) {
        logError((error as Error).message);
      }
    };

    fetchAuth();
  }, [Auth]);

  const handleNextStep = useCallback((stepId: CLAIM_BOOKING_STEPS, data: StepData = {}) => {
    setStep({ stepId, data });
    setGeneralError(null);
  }, []);

  const handleStepTransition = useCallback(
    (claimData: ClaimBookingResult, bookingNumber?: string) => {
      const claimConfirmedStatuses: Partial<EligibilityStatus>[] = [claimStep.READY_TO_CLAIM, claimStep.CLAIMED];

      if (claimData.status === claimStep.READY_TO_CLAIM || claimData.status === claimStep.CLAIMED) {
        handleNextStep(claimStep.SELF_SERVE_ARRAY, { ...claimData, bookingNumber });

        return;
      }

      if (step.stepId === claimStep.SELF_SERVE_ARRAY && claimConfirmedStatuses?.includes(claimData.status)) {
        if (!claimData.sailors?.[0]?.dob) {
          handleNextStep(claimStep.UPDATE_DOB, { ...claimData, bookingNumber });

          return;
        }

        handleNextStep(claimStep.CLAIMED, { ...claimData, bookingNumber });

        return;
      }

      if (claimConfirmedStatuses?.includes(claimData?.status) && !claimData.sailors?.[0]?.dob) {
        handleNextStep(claimStep.UPDATE_DOB, { ...claimData, bookingNumber });

        return;
      }

      if (step.stepId === claimStep.CLAIM_FORM && claimData.status === claimStep.BOOKING_NOT_FOUND) {
        handleNextStep(claimStep.BOOKING_NOT_FOUND, { ...claimData, bookingNumber });

        return;
      }

      handleNextStep(claimData.status, { ...claimData, bookingNumber });
    },
    [step.stepId, handleNextStep],
  );

  const isOnlyOneSailorAvailable = (claimData: ClaimBookingResult) =>
    claimData.sailors?.length === 1 && claimData.noOfGuests === 1;

  const handleBirthDateChange = async (dob: string) => {
    const updatedData = {
      ...step.data,
      selectedDOB: dob,
    };
    await handleDobUpdate(updatedData);
  };

  const handleLookupSailorSelect = useCallback(
    async (selectedSailorsData: SelectedSailorData, stepData?: StepData): Promise<void> => {
      const stepFinalData = stepData || step.data;
      setSelectedSailorData(selectedSailorsData);

      const selectedSailor = getCurrentClaimedSailor(stepFinalData, selectedSailorsData);

      if (!selectedSailor) return;

      const { email, phone } = selectedSailor;
      const hasEmail = Boolean(email);
      const hasPhone = Boolean(phone?.number);
      const fullPhone = phone?.code && phone?.number ? `+${phone.code}${phone.number}` : phone?.number;
      const contactDetails = {
        hasEmail,
        email,
        emailMasked: hasEmail && email ? obfuscateEmail(email) : undefined,
        hasPhone,
        phone: fullPhone,
        phoneMasked:
          hasPhone && phone?.number
            ? `${phone?.code ? `+${phone.code}` : ''} ****${phone.number.slice(-4)}`
            : undefined,
      };

      const hasContactInfo = contactDetails.hasEmail || contactDetails.hasPhone;
      const updatedStepData = {
        ...stepFinalData,
        contactDetails,
        selectedSailorData: selectedSailorsData,
      };

      const nextStep = hasContactInfo ? claimStep.OTP_METHOD_SELECT : claimStep.NO_CONTACT_INFO;

      handleNextStep(nextStep, updatedStepData as SelectedSailorData);
    },
    [step.data, handleNextStep],
  );

  const handleOtpVerification = async (data: StepData & { otpCode: string }): Promise<OtpVerificationResult> => {
    try {
      const selectedSailor = getCurrentClaimedSailor(step.data, selectedSailorData);
      const matchingClientId = selectedSailor?.id || selectedSailor?.clientId;
      const otpType = getOtpType(step.data);
      const otp = data.otpCode;

      const { data: claimBookingResponse } = await claimBookingLookup({
        variables: {
          value: {
            matchingClientId,
            otpType,
            otp,
          },
        },
      });

      const response = claimBookingResponse?.claimBooking;
      const status = response?.status;

      if (status === ClaimBookingOTPStatus.SUCCESSFULLY_CLAIMED) {
        const nextData = {
          ...step.data,
          sailors: response?.sailors || step.data?.sailors,
          reservationDetails: response?.reservationDetails || step.data?.reservationDetails,
          bookingNumber: response?.bookingNumber || step.data?.bookingNumber,
          noOfGuests: response?.noOfGuests ?? step.data?.noOfGuests,
          otpError: undefined,
          otpNetworkError: false,
        } as StepData;

        const currentSelectedSailor = getCurrentClaimedSailor(step.data, selectedSailorData);

        if (currentSelectedSailor && currentUser?.email !== currentSelectedSailor.email) {
          if (!currentSelectedSailor.email && currentUser?.email) {
            await handleEmailMismatchOtp(currentUser?.email);

            return null;
          }

          handleNextStep(claimStep.EMAIL_SELECT_OTP, nextData);

          return null;
        }

        handleNextStep(claimStep.CLAIMED_COMPLETED, nextData);

        return null;
      }

      if (status === ClaimBookingOTPStatus.INVALID_OTP || status === ClaimBookingOTPStatus.ERROR) {
        const apiMessage = response?.message || CLAIM_BOOKING_OTP_RESOURCES.invalidOtp.fallbackString;

        setStep((prevStep) => ({
          ...prevStep,
          data: {
            ...prevStep.data,
            otpError: apiMessage,
            otpNetworkError: false,
          },
        }));

        return { status, message: apiMessage };
      }

      if (status === ClaimBookingOTPStatus.TOO_MANY_ATTEMPTS) {
        handleNextStep(claimStep.OTP_VERIFICATION_LOCKED, data);

        return null;
      }

      if (status === ClaimBookingOTPStatus.BOOKING_NOT_FOUND) {
        handleNextStep(claimStep.BOOKING_NOT_FOUND, step.data);

        return null;
      }

      if (status === ClaimBookingOTPStatus.CACHE_EXPIRED) {
        handleNextStep(claimStep.CACHE_EXPIRED, data);

        return null;
      }

      logWarn(`claimBooking (OTP) returned unexpected status: ${status}`);

      return null;
    } catch (error: unknown) {
      logError(`Error verifying OTP: ${error}`);

      if (isTokenExpiredError(error)) {
        handleNextStep(claimStep.CACHE_EXPIRED, data);
        return null;
      }

      setStep((prevStep) => ({
        ...prevStep,
        data: {
          ...prevStep.data,
          otpNetworkError: true,
        },
      }));

      return null;
    }
  };

  const handleLookupSubmit = useCallback(
    async (value: StepData, token: string, version: 'v2' | 'v3' = 'v3') => {
      const input = {
        bookingNumber: value.bookingNumber,
        user: {
          lastName: value?.user?.lastName,
          birthday: value?.user?.birthday || undefined,
        },
        isManualEntry: value?.isManualEntry,
      };

      const bypassRecaptchaHeader = isRecaptchaTestingEnabled ? { local: 'true' } : {};
      const { data, error } = await runLookupBookingForClaim({
        variables: { value: input },
        context: {
          headers: {
            'X-Recaptcha-Token': token,
            'x-recaptcha-version': version,
            ...bypassRecaptchaHeader,
          },
        },
      });

      if (error) {
        return Promise.reject(error);
      }

      const lookupResult = data?.lookupBookingForClaim;
      if (!lookupResult) return null;

      const mapped = mapLookupToClaimResult(lookupResult);
      const bookingNumber = lookupResult.bookingNumber || value.bookingNumber;

      if (mapped.status !== claimStep.READY_TO_CLAIM && !isOnlyOneSailorAvailable(mapped)) {
        handleStepTransition(mapped, bookingNumber);

        return null;
      }

      // Auto-select sailor if there's only one
      if (
        isOnlyOneSailorAvailable(mapped) &&
        ([claimStep.SELF_SERVE_ARRAY, claimStep.READY_TO_CLAIM] as Partial<EligibilityStatus[]>).includes(mapped.status)
      ) {
        const sailorData = mapSailorToSelectedSailorData(mapped?.sailors?.[0] as MatchingClient, bookingNumber!);
        handleLookupSailorSelect(sailorData, { ...mapped, bookingNumber });

        return null;
      }

      handleNextStep(claimStep.OTP_METHOD_SELECT, { ...mapped, bookingNumber });

      return null;
    },
    [
      isRecaptchaTestingEnabled,
      runLookupBookingForClaim,
      handleStepTransition,
      handleNextStep,
      handleLookupSailorSelect,
    ],
  );

  const handleOtpMethodSelect = async (data: StepData & { selectedMethod: ContactMethod }) => {
    try {
      const selectedSailor = getCurrentClaimedSailor(step.data, selectedSailorData);
      const matchingClientId = selectedSailor?.id || selectedSailor?.clientId;
      const otpType = getOtpType(data);

      const { data: otpResponse } = await generateClaimBookingOTP({
        variables: {
          value: {
            matchingClientId,
            otpType,
          },
        },
      });

      const status = otpResponse?.generateClaimBookingOTP?.status;

      if (status === OTPStatus.SUCCESS) {
        handleNextStep(claimStep.OTP_SENT, { ...step.data, ...data, otpError: undefined, otpNetworkError: false });

        return;
      }

      if (status === OTPStatus.TOO_MANY_ATTEMPTS || status === OTPStatus.ERROR) {
        handleNextStep(claimStep.OTP_VERIFICATION_LOCKED, data);

        return;
      }

      if (status === OTPStatus.CACHE_EXPIRED) {
        handleNextStep(claimStep.CACHE_EXPIRED, data);

        return;
      }

      logWarn(`generateClaimBookingOTP returned non-success status: ${status}`);
      setGeneralError(CONNECTION_ERROR_MESSAGE);
    } catch (error: unknown) {
      logError(`Error sending OTP: ${error}`);

      if (isTokenExpiredError(error)) {
        handleNextStep(claimStep.CACHE_EXPIRED, data);
        return;
      }

      setGeneralError(CONNECTION_ERROR_MESSAGE);
    }
  };

  const handleResendOtp = () => {
    if (step.data.selectedMethod) {
      handleOtpMethodSelect(step.data as StepData & { selectedMethod: ContactMethod });
    }
  };

  const {
    validateWithRecaptcha: handleClaimSubmitWithRecaptcha,
    RecaptchaV2Component,
    isUseRecaptchaV2,
  } = useRecaptchaValidation(async (value: StepData, rcToken: string, version: 'v2' | 'v3' = 'v3') => {
    try {
      await handleLookupSubmit(value, rcToken, version);
    } catch (error: unknown) {
      if (isTokenExpiredError(error)) {
        handleNextStep(claimStep.CACHE_EXPIRED, value);
        return;
      }

      // Re-throw other errors to be handled by the recaptcha validation hook
      throw error;
    }
  }, 'lookupBookingForClaim');

  const handleProceed = (stepId: CLAIM_BOOKING_STEPS) => (value: StepData) => {
    const isDateOfBirthMissing = !value?.selectedDOB && !value?.sailors?.[0]?.dob;
    const needsDobUpdate = stepId === claimStep.CLAIMED && isDateOfBirthMissing;
    let nextStep = needsDobUpdate ? claimStep.UPDATE_DOB : stepId;
    if (nextStep === claimStep.CLAIMED) {
      nextStep = claimStep.SELF_SERVE_ARRAY;
    }

    handleNextStep(nextStep, {
      ...value,
      reservationDetails: step.data?.reservationDetails,
      firstName: value?.firstName || step.data?.firstName,
      noOfGuests: value?.noOfGuests || step.data?.noOfGuests,
    });
  };

  const reloadReservation = () => {
    backToLogin();
  };

  const connectBooking = () => {
    handleNextStep(DETAILS_MANUAL_ENTRY_FORM);
  };

  const handleBackFromOtpMethodSelect = () => {
    handleNextStep(DETAILS_MANUAL_ENTRY_FORM);
  };

  const handleBackFromOtpSent = () => {
    handleNextStep(claimStep.OTP_METHOD_SELECT, step.data);
  };

  const handleProfileUpdate = async (data: StepData) => {
    try {
      const claimedSailor = getCurrentClaimedSailor(data, selectedSailorData);
      const matchingClientId = claimedSailor?.id || data?.sailors?.[0]?.id;

      if (!matchingClientId) {
        const error = 'matchingClientId is undefined for postClaimSailorProfileUpdate';
        logError(error);
        setGeneralError(CONNECTION_ERROR_MESSAGE);

        return false;
      }

      const shouldUpdateEmail =
        Boolean(currentUser?.email) && (!claimedSailor?.email || claimedSailor.email !== currentUser?.email);
      const shouldUpdateDob = Boolean(data.selectedDOB);

      // Only call mutation if there's something to update
      if (!shouldUpdateEmail && !shouldUpdateDob) {
        return true;
      }

      const { data: updateResponse, errors } = await postClaimSailorProfileUpdate({
        variables: {
          value: {
            matchingClientId,
            updateProfileEmail: shouldUpdateEmail,
            ...(shouldUpdateDob && { selectedDOB: data.selectedDOB }),
          },
        },
      });

      const status = updateResponse?.postClaimSailorProfileUpdate?.status;

      if (status === 'CACHE_EXPIRED') {
        handleNextStep(claimStep.CACHE_EXPIRED, data);

        return false;
      }

      if (!updateResponse?.postClaimSailorProfileUpdate?.success || errors?.length) {
        logError('Failed to update sailor profile');
        setGeneralError(CONNECTION_ERROR_MESSAGE);

        return false;
      }

      return true;
    } catch (error: unknown) {
      logError(`Error updating profile: ${error}`);

      if (isTokenExpiredError(error)) {
        handleNextStep(claimStep.CACHE_EXPIRED, data);
        return false;
      }

      setGeneralError(CONNECTION_ERROR_MESSAGE);

      return false;
    }
  };

  const handleEmailMismatchOtp = async (selectedEmail: string) => {
    try {
      const updatedData = {
        ...step.data,
        selectedEmail,
      };

      const selectedSailor = getCurrentClaimedSailor(step.data, selectedSailorData);
      const hasDob = selectedSailor?.dob;

      if (!hasDob) {
        handleNextStep(claimStep.UPDATE_DOB, updatedData);

        return;
      }

      const success = await handleProfileUpdate(updatedData);

      if (success) {
        handleNextStep(claimStep.CLAIMED_COMPLETED, updatedData);
      }
    } catch (error: unknown) {
      logError(`Error handling email mismatch: ${error}`);

      if (isTokenExpiredError(error)) {
        handleNextStep(claimStep.CACHE_EXPIRED, {});
        return;
      }

      setGeneralError(CONNECTION_ERROR_MESSAGE);
    }
  };

  const handleDobUpdate = async (data: StepData) => {
    try {
      const success = await handleProfileUpdate(data);

      if (success) {
        handleNextStep(claimStep.CLAIMED_COMPLETED, data);
      }
    } catch (error: unknown) {
      logError(`Error updating DOB: ${error}`);

      if (isTokenExpiredError(error)) {
        handleNextStep(claimStep.CACHE_EXPIRED, data);
        return;
      }

      setGeneralError(CONNECTION_ERROR_MESSAGE);
    }
  };

  const getContentClassName = () => {
    if (
      step.stepId === claimStep.CLAIMED_COMPLETED ||
      step.stepId === claimStep.EMAIL_SELECT_OTP ||
      step.stepId === claimStep.UPDATE_DOB
    ) {
      return `${styles.modalContainer} ${styles.modalContainerLarge}`;
    }

    return styles.modalContainer;
  };

  const isLoading =
    loadingLookup || loadingGenerateOtp || loadingClaimLookup || !isReCaptchaLoaded || loadingPostClaimProfile;

  return (
    <div id="ReactivationOTPFlowContainer" className={cn([styles.reactivationFlowContainer, getContentClassName()])}>
      {(generalError || step.data.otpNetworkError) && (
        <NotificationError
          icon
          mainText="Timeout 📝"
          subText={CONNECTION_ERROR_MESSAGE}
          className={styles.cabNotification}
        />
      )}
      <WizardContainer selectedStepId={step.stepId} className={cn(styles.content, styles.otpContent)}>
        <ReclaimAccount
          tabId={RECLAIM_ACCOUNT}
          onSubmit={handleProceed(DETAILS_MANUAL_ENTRY_FORM)}
          onBackClick={backToLogin}
        />
        <ClaimBookingForm
          tabId={DETAILS_MANUAL_ENTRY_FORM}
          onSubmit={handleClaimSubmitWithRecaptcha}
          onCancel={backToLogin}
          isUseRecaptchaV2={isUseRecaptchaV2}
          RecaptchaV2Component={RecaptchaV2Component}
        />
        <ClaimBookingSailorSelect
          tabId={claimStep.SELF_SERVE_ARRAY}
          data={step.data}
          onSubmit={handleLookupSailorSelect}
          onCancel={backToLogin}
          isUseRecaptchaV2={isUseRecaptchaV2}
          RecaptchaV2Component={RecaptchaV2Component}
        />
        <ClaimBookingOtpMethodSelect
          tabId={claimStep.OTP_METHOD_SELECT}
          data={step.data}
          onSubmit={handleOtpMethodSelect}
          onBack={handleBackFromOtpMethodSelect}
          onCancel={backToLogin}
        />
        <ClaimBookingOtpSent
          tabId={claimStep.OTP_SENT}
          data={step.data}
          onSubmit={handleOtpVerification}
          onBack={handleBackFromOtpSent}
          onCancel={backToLogin}
          onResendOtp={handleResendOtp}
        />
        <ClaimBookingOtpLocked tabId={claimStep.OTP_VERIFICATION_LOCKED} onCancel={backToLogin} />
        <ClaimBookingEmailSelectOTP
          tabId={claimStep.EMAIL_SELECT_OTP}
          data={step.data}
          sessionUserEmail={currentUser?.email || ''}
          onSubmit={handleEmailMismatchOtp}
          onCancel={backToLogin}
        />
        <ClaimBookingDobUpdateOTP
          tabId={claimStep.UPDATE_DOB}
          data={step.data}
          onSubmit={handleBirthDateChange}
          onCancel={backToLogin}
        />
        <ClaimBookingDetailsConfirmation
          tabId={claimStep.CLAIMED}
          data={step.data as any}
          onSubmit={handleClaimSubmitWithRecaptcha}
          onCancel={backToLogin}
          currentUser={currentUser}
          isUseRecaptchaV2={isUseRecaptchaV2}
          RecaptchaV2Component={RecaptchaV2Component}
        />
        <ClaimBookingOTPSuccess tabId={claimStep.CLAIMED_COMPLETED} stepData={step.data} onClose={backToLogin} />
        <ClaimBookingSessionExpired tabId={claimStep.CACHE_EXPIRED} onCancel={backToLogin} />
        <ClaimBookingError tabId={claimStep.BOOKING_NOT_FOUND} onClose={backToLogin} />
        <NoContactDetail tabId={claimStep.NO_CONTACT_INFO} onCancel={backToLogin} />
        <ClaimImportingInProgress tabId={IMPORT_IN_PROGRESS} reloadReservation={reloadReservation} />
        <ClaimImportError tabId={IMPORT_ERROR} connectBooking={connectBooking} />
      </WizardContainer>
      {isLoading && (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ReactivationOTPFlowContainer;
